import { useEffect, useState } from "react";
import {
  Sparkle,
  ArrowClockwise,
  TrendUp,
  ChartLineUp,
  Lightbulb,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtMoney } from "../../lib/finance-format";
import { useTheme } from "../../contexts/ThemeContext";

const MONTHS_OPTIONS = [
  { value: 6, label: "6 meses" },
  { value: 12, label: "12 meses" },
  { value: 24, label: "24 meses" },
];

const SCENARIOS = [
  { value: "baseline", label: "Base", emoji: "📊" },
  { value: "optimistic", label: "Optimista", emoji: "📈" },
  { value: "pessimistic", label: "Pesimista", emoji: "📉" },
];

const MONTH_ABBR_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const fmtMonth = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const y = ym.slice(2, 4);
  const m = parseInt(ym.slice(5, 7), 10);
  return `${MONTH_ABBR_ES[m - 1]} '${y}`;
};

function TooltipContent({ active, payload, label, isDark }) {
  if (!active || !payload?.length) return null;
  const bg = isDark ? "#12131C" : "#FFFFFF";
  const border = isDark ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.16)";
  return (
    <div style={{ background: bg, border: `1px solid ${border}` }} className="rounded-lg px-3 py-2 shadow-2xl text-[11px] font-mono">
      <div className="font-heading font-bold text-white mb-1">{fmtMonth(label)}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-white/60">{p.name}:</span>
          <span className="font-bold text-white tabular-nums">{fmtMoney(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * AI cash-flow forecast section, appended below the historic charts in Reports.
 */
export default function ForecastCard({ scope }) {
  const { isDark } = useTheme();
  const [months, setMonths] = useState(6);
  const [scenario, setScenario] = useState("baseline");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async (m = months, s = scenario) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.post(
        "/finance/forecast",
        { months: m, scope: scope || "all", scenario: s },
        { timeout: 60000 },
      );
      setData(res);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "No se pudo generar forecast";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // auto-run once when first shown; guard against StrictMode double-invoke
    let cancelled = false;
    const t = setTimeout(() => {
      if (!cancelled) run(months, scenario);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [scope]);

  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)";
  const axisColor = isDark ? "rgba(255,255,255,0.35)" : "rgba(15,23,42,0.45)";

  return (
    <section className="rounded-lg border border-white/8 bg-black/20 p-4 md:p-5" data-testid="forecast-card">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#8B5CF6]">
            AI FORECAST · CLAUDE SONNET 4.6
          </div>
          <h3 className="text-base md:text-lg font-heading font-bold text-white">
            Proyección de cash flow
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Scenario switcher */}
          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-black/40 border border-white/8">
            {SCENARIOS.map((s) => (
              <button
                key={s.value}
                data-testid={`scenario-${s.value}`}
                onClick={() => { setScenario(s.value); run(months, s.value); }}
                className={`px-2 py-1 text-[10px] font-mono uppercase tracking-widest font-bold rounded ${
                  scenario === s.value ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70"
                }`}
                title={s.label}
              >
                <span className="mr-1">{s.emoji}</span>{s.label}
              </button>
            ))}
          </div>
          <select
            data-testid="forecast-months-select"
            value={months}
            onChange={(e) => { setMonths(parseInt(e.target.value, 10)); run(parseInt(e.target.value, 10), scenario); }}
            className="bg-black/40 border border-white/8 rounded-lg px-2 py-1 text-[11px] font-mono text-white outline-none"
          >
            {MONTHS_OPTIONS.map((m) => (
              <option key={m.value} value={m.value} className="bg-[#0F1017]">{m.label}</option>
            ))}
          </select>
          <button
            data-testid="forecast-refresh"
            onClick={() => run()}
            disabled={loading}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/8 text-white/60 flex items-center justify-center"
            title="Regenerar"
          >
            <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-16 text-center text-xs font-mono text-white/40">
          <Sparkle size={22} weight="fill" className="mx-auto mb-2 text-[#8B5CF6] animate-pulse" />
          Claude está proyectando tu futuro financiero...
          <div className="text-[10px] text-white/30 mt-1">Puede tardar hasta 30 segundos</div>
        </div>
      ) : error ? (
        <div className="py-10 text-center" data-testid="forecast-error">
          <div className="text-xs font-mono text-red-300 mb-3">{error}</div>
          <button
            onClick={() => run()}
            data-testid="forecast-retry"
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-heading font-bold inline-flex items-center gap-2"
          >
            <ArrowClockwise size={12} weight="bold" />
            Reintentar
          </button>
        </div>
      ) : data?.months?.length ? (
        <>
          {data.assumptions && (
            <div className="mb-3 p-3 rounded-lg bg-[#8B5CF6]/10 border border-[#8B5CF6]/25 text-[11px] font-mono text-white/70 leading-relaxed">
              <Lightbulb size={11} weight="fill" className="inline mr-1.5 text-[#8B5CF6]" />
              <span className="text-[#8B5CF6] font-bold uppercase tracking-widest text-[9px] mr-1">Supuestos:</span>
              {data.assumptions}
            </div>
          )}

          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data.months} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
              <defs>
                <linearGradient id="bandFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: axisColor }}
                tickFormatter={fmtMonth}
                tickLine={false}
                axisLine={{ stroke: gridColor }}
              />
              <YAxis
                tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: axisColor }}
                tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
                tickLine={false}
                axisLine={{ stroke: gridColor }}
              />
              <Tooltip content={<TooltipContent isDark={isDark} />} />
              <Area type="monotone" dataKey="high" name="Alto" stroke="none" fill="url(#bandFill)" />
              <Area type="monotone" dataKey="low" name="Bajo" stroke="none" fill={isDark ? "#12131C" : "#F5F7FB"} />
              <Line type="monotone" dataKey="net" name="Neto" stroke="#8B5CF6" strokeWidth={2.5} dot={{ fill: "#8B5CF6", r: 3 }} />
              <Line type="monotone" dataKey="cumulative" name="Acumulado" stroke="#10B981" strokeWidth={2} strokeDasharray="4 4" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>

          {data.insight && (
            <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/25">
              <div className="flex items-start gap-2">
                <Lightbulb size={14} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
                <div className="text-[12px] font-mono text-emerald-200 dark:text-emerald-200 leading-relaxed">
                  <span className="font-bold uppercase tracking-widest text-[9px] text-emerald-400 mr-1">INSIGHT:</span>
                  {data.insight}
                </div>
              </div>
            </div>
          )}

          {/* Compact numeric table */}
          <div className="mt-3 max-h-40 overflow-y-auto rounded-lg border border-white/6 text-[11px] font-mono">
            <div className="grid grid-cols-4 gap-2 px-3 py-1.5 bg-black/40 text-[9px] uppercase tracking-widest text-white/40 border-b border-white/6">
              <div>Mes</div>
              <div className="text-right">Ingresos</div>
              <div className="text-right">Gastos</div>
              <div className="text-right">Neto acumulado</div>
            </div>
            <div className="divide-y divide-white/6">
              {data.months.map((m) => (
                <div key={m.month} className="grid grid-cols-4 gap-2 px-3 py-1.5">
                  <div className="text-white/70">{fmtMonth(m.month)}</div>
                  <div className="text-right text-emerald-400 tabular-nums">+{fmtMoney(m.income)}</div>
                  <div className="text-right text-red-400 tabular-nums">-{fmtMoney(m.expenses)}</div>
                  <div className={`text-right tabular-nums font-bold ${m.cumulative >= 0 ? "text-white" : "text-red-400"}`}>
                    {m.cumulative >= 0 ? "+" : ""}{fmtMoney(m.cumulative)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-xs font-mono text-white/40">
          Sin datos suficientes. Sube algún estado bancario o agrega ingresos/gastos.
        </div>
      )}
    </section>
  );
}
